import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    useTheme,
    Alert,
    Link,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';

const API_URL = 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

const ForgotPasswordPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess(true);
                // Store email for reset page
                sessionStorage.setItem('resetEmail', email);
                // Redirect to reset password page after 2 seconds
                setTimeout(() => {
                    navigate('/reset-password');
                }, 2000);
            } else {
                setError(data.error || 'Failed to send reset code');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
            }}
        >
            <Container maxWidth="sm">
                <Card sx={{ borderRadius: '16px' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    border: `3px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'transparent',
                                    mb: 2,
                                }}
                            >
                                <LockResetIcon sx={{ fontSize: '2.5rem', color: theme.palette.primary.main }} />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                Forgot Password?
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', mt: 1 }}>
                                Enter your email and we'll send you a reset code
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
                                {error}
                            </Alert>
                        )}

                        {success ? (
                            <Alert severity="success" sx={{ mb: 3, borderRadius: '8px' }}>
                                Reset code sent! Check your email. Redirecting...
                            </Alert>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="Email Address"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    sx={{ mb: 3 }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={loading}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: '30px',
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {loading ? 'Sending...' : 'Send Reset Code'}
                                </Button>
                            </form>
                        )}

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography sx={{ color: 'text.secondary' }}>
                                Remember your password?{' '}
                                <Link
                                    href="/login"
                                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    Sign In
                                </Link>
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ForgotPasswordPage;
